import {
  RiComputerLine,
  RiExpandUpDownLine,
  RiLogoutBoxRLine,
  RiMoonLine,
  RiSettings3Line,
  RiSunLine,
  RiUserAddLine,
} from "@remixicon/react";
import { Avatar, AvatarFallback } from "@screener/uikit/components/avatar";
import { Badge } from "@screener/uikit/components/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@screener/uikit/components/dropdown-menu";
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "@screener/uikit/components/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import type { ThemeChoice } from "@screener/uikit/hooks/use-theme";
import type { KeyboardEvent, ReactNode } from "react";
import { useRef } from "react";
import { INVITES_MENU_LABEL } from "@/invites/invites-panel";
import type { AccessPlan } from "@/lib/auth-api";
import { planLabel, planMenuLabel, planTierLabel } from "@/lib/plan-gate";

/** Выбор темы в меню: подпись и значок у каждого, порядок — как в ТЗ §56 п. 2. */
export const THEME_OPTIONS: ReadonlyArray<{ value: ThemeChoice; label: string; icon: ReactNode }> =
  [
    { value: "light", label: "Светлая", icon: <RiSunLine /> },
    { value: "dark", label: "Тёмная", icon: <RiMoonLine /> },
    { value: "system", label: "Как в системе", icon: <RiComputerLine /> },
  ];

/**
 * Инициалы для аватара: первые две буквы логина в верхнем регистре. Логин в
 * §6.6 — одно слово, делить его на имя и фамилию не на чем (design.md решение 5).
 */
export function initialsOf(login: string): string {
  return Array.from(login).slice(0, 2).join("").toUpperCase();
}

/** Подпись выбранной темы под словом «Тема»; неизвестный выбор не подписывается. */
export function themeLabelOf(choice: ThemeChoice): string {
  return THEME_OPTIONS.find((option) => option.value === choice)?.label ?? "";
}

/**
 * Следующая кнопка радиогруппы по стрелке: вправо и влево с переходом через
 * край; прочие клавиши группа не трогает, их разбирает меню.
 */
export function nextThemeIndex(current: number, key: string, count: number): number | null {
  if (key === "ArrowRight") {
    return (current + 1) % count;
  }
  if (key === "ArrowLeft") {
    return (current - 1 + count) % count;
  }
  return null;
}

/**
 * Кнопка темы 30×26. Выбранная — `bg-accent text-foreground`; невыбранная в
 * фокусе или под указателем — `bg-foreground/5`; фокус с клавиатуры — кольцо
 * 2 px `ring`. Базовые `focus:bg-accent` и значок-индикатор пункта UIKit
 * перекрыты: выбор должен читаться отдельно от фокуса.
 */
const THEME_BUTTON_CLASS =
  "grid h-[26px] w-[30px] place-items-center gap-0 rounded-sm p-0 text-muted-foreground focus:bg-foreground/5 focus:text-foreground hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring aria-checked:bg-accent aria-checked:text-foreground aria-checked:focus:bg-accent aria-checked:hover:bg-accent [&_svg:not([class*='size-'])]:size-3.5 [&>[data-slot=dropdown-menu-radio-item-indicator]]:hidden";

/**
 * Подпись тарифа. Развёрнутая панель — плашка-тег в строке. Свёрнутая —
 * компактная плашка (кегль 8 px) поверх нижнего края аватара по центру;
 * непрозрачный фон из темы, чтобы читаться на инициалах. Тариф уже назван в
 * `aria-label` кнопки, поэтому плашка скрыта от читалки.
 */
const PLAN_TIER_CLASS =
  "shrink-0 group-data-[collapsible=icon]:absolute group-data-[collapsible=icon]:bottom-0.5 group-data-[collapsible=icon]:left-1/2 group-data-[collapsible=icon]:h-auto group-data-[collapsible=icon]:-translate-x-1/2 group-data-[collapsible=icon]:bg-background group-data-[collapsible=icon]:px-0.5 group-data-[collapsible=icon]:text-[8px] group-data-[collapsible=icon]:leading-none group-data-[collapsible=icon]:tracking-normal";

/** Зазор между краем панели и меню, px (§111 п. 6). */
const MENU_GAP_PX = 8;
/** Сдвиг по оси выравнивания, который ставит нижний край меню в 8 px от низа панели. */
const MENU_BOTTOM_ALIGN_OFFSET_PX = 8;

/** Пункты меню: 32 px, значок 14 px приглушённый, подпись 13 px (§111 п. 6). */
const MENU_ITEM_CLASS =
  "h-8 gap-2.5 rounded-sm px-2 py-0 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:text-muted-foreground";

export interface NavUserProps {
  login: string;
  /** Право приглашать и включённый `signup-invite` (§104 пп. 5–6): без обоих пункта нет. */
  canInvite: boolean;
  onOpenInvites: () => void;
  themeChoice: ThemeChoice;
  onThemeChoose: (choice: ThemeChoice) => void;
  /** Перейти на страницу «Настройки» (§127 п. 7): Telegram, терминал и скрытые монеты. */
  onOpenSettings: () => void;
  onLogout: () => void;
  /**
   * Элемент панели, к которому привязано меню на широком экране: так оно
   * стоит одинаково в развёрнутой и свёрнутой панели (stage9-nav-redesign, Д5).
   * `null` — якоря нет, меню открывается у кнопки.
   */
  menuAnchor?: Element | null;
  /**
   * Действующий тариф для подписи (`FREE` / `TRADER` / `PRO`). `null` или не
   * передан — подписи нет: биллинг выключен или доступ ещё не прочитан.
   */
  tier?: AccessPlan | null;
}

/**
 * Блок пользователя внизу панели (ТЗ §9 1.43, §56 п. 2, §111 пп. 5–6):
 * кнопка профиля и меню с темой, настройками, приглашением и выходом.
 * Часть экрана, а не UIKit: знает про страницы клиента и выход.
 */
export function NavUser({
  login,
  canInvite,
  onOpenInvites,
  themeChoice,
  onThemeChoose,
  onOpenSettings,
  onLogout,
  menuAnchor = null,
  tier = null,
}: NavUserProps) {
  const { isMobile, setOpenMobile } = useSidebar();
  const anchored = !isMobile && menuAnchor !== null;

  /** На узком экране выехавшая панель закрывается, как при выборе пункта: иначе окно легло бы вторым листом поверх неё. */
  const closeMobilePanel = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        {/* Имя кнопки — логин, а не «инициалы + логин»: в свёрнутой панели
            видны одни инициалы, и читалке незачем произносить их отдельно.
            Развёрнута — строка 40 px с логином и шевроном, свёрнута — один
            аватар (§111 п. 5). */}
        <DropdownMenuTrigger
          render={
            <SidebarMenuButton
              aria-label={tier === null ? login : `${login}, тариф ${planLabel(tier)}`}
              className="h-10 gap-2.5 rounded-md px-1.5 py-0 text-[13px] font-medium hover:bg-foreground/5 hover:text-foreground focus-visible:ring-ring data-popup-open:bg-foreground/6 group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0!"
            />
          }
        >
          <Avatar className="size-7 rounded-sm after:hidden">
            <AvatarFallback className="rounded-sm bg-secondary text-[11px] font-semibold text-secondary-foreground">
              {initialsOf(login)}
            </AvatarFallback>
          </Avatar>
          <span className="truncate group-data-[collapsible=icon]:hidden">{login}</span>
          {tier === null ? null : (
            <Badge
              data-slot="plan-tier"
              variant="outline-muted"
              size="tag"
              aria-hidden="true"
              className={PLAN_TIER_CLASS}
            >
              {planTierLabel(tier)}
            </Badge>
          )}
          <RiExpandUpDownLine
            aria-hidden="true"
            className="ml-auto size-3.5 shrink-0 text-muted-foreground group-data-[collapsible=icon]:hidden"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-62 p-1"
          side={isMobile ? "bottom" : "right"}
          align="end"
          sideOffset={anchored ? MENU_GAP_PX : 4}
          alignOffset={anchored ? MENU_BOTTOM_ALIGN_OFFSET_PX : 0}
          anchor={anchored ? menuAnchor : undefined}
        >
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex items-center gap-2.5 px-2 pt-2 pb-2.5 text-[13px] font-semibold text-foreground">
              <Avatar aria-hidden="true" className="size-8 rounded-sm after:hidden">
                <AvatarFallback className="rounded-sm bg-secondary text-xs font-semibold text-secondary-foreground">
                  {initialsOf(login)}
                </AvatarFallback>
              </Avatar>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate">{login}</span>
                {tier === null ? null : (
                  <span className="truncate text-[11px] leading-none font-normal text-muted-foreground">
                    {planMenuLabel(tier)}
                  </span>
                )}
              </span>
            </DropdownMenuLabel>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <ThemeRow choice={themeChoice} onChoose={onThemeChoose} />
          <DropdownMenuSeparator />
          <DropdownMenuGroup className="py-1">
            <DropdownMenuItem
              className={MENU_ITEM_CLASS}
              onClick={() => {
                closeMobilePanel();
                onOpenSettings();
              }}
            >
              <RiSettings3Line />
              Настройки
            </DropdownMenuItem>
            {canInvite ? (
              <DropdownMenuItem
                className={MENU_ITEM_CLASS}
                onClick={() => {
                  closeMobilePanel();
                  onOpenInvites();
                }}
              >
                <RiUserAddLine />
                {INVITES_MENU_LABEL}
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup className="pt-1">
            <DropdownMenuItem className={MENU_ITEM_CLASS} onClick={onLogout}>
              <RiLogoutBoxRLine />
              Выйти
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * Строка темы: слово «Тема», под ним название выбранной и три кнопки-значка
 * группой пунктов-переключателей `DropdownMenuRadioGroup` (Д6): роль
 * `menuitemradio`, выбранная отмечена `aria-checked`, нажатие меню не
 * закрывает. Стрелки влево и вправо двигают выбор внутри группы, вверх и
 * вниз уходят к пунктам-действиям меню.
 */
function ThemeRow({
  choice,
  onChoose,
}: {
  choice: ThemeChoice;
  onChoose: (choice: ThemeChoice) => void;
}) {
  const buttons = useRef<Array<HTMLElement | null>>([]);

  const handleValueChange = (value: unknown) => {
    const option = THEME_OPTIONS.find((candidate) => candidate.value === value);
    if (option !== undefined) {
      onChoose(option.value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>, index: number) => {
    const next = nextThemeIndex(index, event.key, THEME_OPTIONS.length);
    const option = next === null ? undefined : THEME_OPTIONS[next];
    if (next !== null && option !== undefined) {
      // Стрелки влево и вправо разбирает группа, а не меню.
      event.preventDefault();
      event.stopPropagation();
      onChoose(option.value);
      buttons.current[next]?.focus();
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      // Штатный обход меню Base UI идёт по соседним кнопкам темы (проверено
      // e2e: вниз от «Тёмной» приходит на «Как в системе»), а строка темы по
      // Д6 — одна остановка. Поэтому вверх и вниз уводим к пунктам-действиям
      // меню; они отличаются ролью `menuitem` от кнопок темы (`menuitemradio`).
      const actions = Array.from(
        event.currentTarget
          .closest("[role=menu]")
          ?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? [],
      );
      const target = event.key === "ArrowDown" ? actions[0] : actions[actions.length - 1];
      if (target !== undefined) {
        event.preventDefault();
        event.stopPropagation();
        target.focus();
      }
    }
  };

  return (
    <div data-slot="theme-row" className="flex h-11 items-center justify-between gap-3 px-2">
      <span className="flex flex-col gap-1">
        <span className="text-[13px] leading-none">Тема</span>
        <span className="text-[11px] leading-none text-muted-foreground">
          {themeLabelOf(choice)}
        </span>
      </span>
      <DropdownMenuRadioGroup
        aria-label="Тема"
        value={choice}
        onValueChange={handleValueChange}
        className="flex gap-0.5 rounded-md border border-border bg-background p-0.5"
      >
        {THEME_OPTIONS.map((option, index) => (
          <Tooltip key={option.value}>
            <TooltipTrigger
              render={
                <DropdownMenuRadioItem
                  ref={(element) => {
                    buttons.current[index] = element;
                  }}
                  render={<button type="button" />}
                  value={option.value}
                  aria-label={option.label}
                  closeOnClick={false}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  className={THEME_BUTTON_CLASS}
                />
              }
            >
              {option.icon}
            </TooltipTrigger>
            <TooltipContent side="top">{option.label}</TooltipContent>
          </Tooltip>
        ))}
      </DropdownMenuRadioGroup>
    </div>
  );
}
